// src/features/admin-options/components/SystemInfoView.tsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Alert as MuiAlert,
  Paper,
} from '@mui/material';
import {
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  VpnKey as SecretIcon,
  Analytics as StatsIcon,
  Cloud as CloudIcon,
  HealthAndSafety as HealthIcon,
  DataObject as DataObjectIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';
import { tableHeadCellSx } from '@/components/common/tableHeadCellSx';
import type { SystemInfo, SystemJob } from '@/types/models';

const SystemInfoView: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [jobs, setJobs] = useState<SystemJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSystemInfo = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      const { data } = await dataProvider.getSystemInfo();
      setSystemInfo(data);
    } catch (err) {
      console.error('Error loading system information:', err);
      setError('Failed to load system information');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dataProvider]);

  const fetchJobs = useCallback(async () => {
    try {
      const { data } = await dataProvider.getSystemJobs();
      setJobs(data);
    } catch (err) {
      console.error('Error loading jobs:', err);
    }
  }, [dataProvider]);

  useEffect(() => {
    fetchSystemInfo();
    fetchJobs();

    const interval = setInterval(() => {
      fetchSystemInfo();
      fetchJobs();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchSystemInfo, fetchJobs]);

  const handleTriggerJob = async (jobId: string, jobName: string) => {
    try {
      await dataProvider.triggerSystemJob(jobId);
      notify(translate('resources.system.jobs.triggerSuccess'), { type: 'success' });
      setTimeout(() => { fetchJobs(); }, 1000);
    } catch {
      notify(translate('resources.system.jobs.triggerError'), { type: 'error' });
    }
  };

  const handleRefresh = () => {
    fetchSystemInfo();
    fetchJobs();
  };

  const handleCopySeqFilter = () => {
    if (systemInfo?.logging?.seqFilter) {
      navigator.clipboard.writeText(systemInfo.logging.seqFilter);
      notify(translate('resources.system.logging.filterCopied'), { type: 'success' });
    }
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (loading && !systemInfo) {
    return (
      <Paper elevation={0} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, borderRadius: 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} thickness={3} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {translate('common.labels.loading')}
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Error state
  if (error && !systemInfo) {
    return (
      <MuiAlert
        severity="error"
        sx={{ borderRadius: 1 }}
        action={
          <IconButton size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </MuiAlert>
    );
  }

  if (!systemInfo) return null;

  // ── Reusable components ──

  const InfoRow = ({ label, value, isStatus = false }: { label: string; value: React.ReactNode; isStatus?: boolean }) => (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25,
      '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
    }}>
      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontWeight: 400 }}>
        {label}
      </Typography>
      {isStatus ? value : (
        <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', fontWeight: 500 }}>
          {value}
        </Typography>
      )}
    </Box>
  );

  const CardHeader = ({ icon, title, action }: { icon: React.ReactNode; title: string; action?: React.ReactNode }) => (
    <Box sx={{
      p: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider',
      backgroundColor: 'grey.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.95rem', fontWeight: 600, color: 'text.primary' }}>
        {icon}
        {title}
      </Typography>
      {action}
    </Box>
  );

  const SectionLabel = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
    <Typography sx={{
      color: 'text.secondary', mb: 2, display: 'flex', alignItems: 'center', gap: 1,
      fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em',
    }}>
      {icon}
      {label}
    </Typography>
  );

  // ── Technology concealment ──
  // The API returns some values verbatim (database provider class name,
  // runtime version string, machine host name, secret display names) that
  // would otherwise name an internal technology directly in the UI. These
  // helpers map them to a neutral, translated label without hiding the row.

  const describeDatabaseProvider = (raw: string): string => {
    const lower = raw.toLowerCase();
    if (lower.includes('postgres') || lower.includes('npgsql') || lower.includes('sqlserver') || lower.includes('mysql') || lower.includes('sqlite')) {
      return translate('resources.system.database.categoryRelational');
    }
    if (lower.includes('mongo')) {
      return translate('resources.system.database.categoryDocument');
    }
    return translate('resources.system.database.categoryOther');
  };

  const formatRuntimeVersion = (raw: string): string => raw.replace(/^\.NET\s*/i, '').trim() || raw;

  const describeMachineName = (raw: string): string => {
    // A stable, non-identifying token derived from the raw host name — the
    // host/OS-derived naming convention itself never reaches the UI.
    let hash = 0;
    for (let i = 0; i < raw.length; i += 1) hash = (hash * 31 + raw.charCodeAt(i)) >>> 0;
    return `#${hash.toString(36).toUpperCase().slice(0, 6)}`;
  };

  const describeSecretName = (raw: string): string => raw
    .replace(/keycloak/gi, translate('resources.system.secrets.neutralTerms.identityProvider'))
    .replace(/postgres(?:ql)?|npgsql/gi, translate('resources.system.secrets.neutralTerms.database'))
    .replace(/\bseq\b/gi, translate('resources.system.secrets.neutralTerms.logServer'));

  const cardSx = { mb: 2, borderRadius: 1, boxShadow: 'none', border: '1px solid', borderColor: 'divider' };

  const envChipColor = (env: string) => {
    if (env === 'Production') return { bg: 'rgba(34, 197, 94, 0.1)', color: '#16a34a' };
    if (env === 'Staging') return { bg: 'rgba(251, 191, 36, 0.1)', color: '#ea580c' };
    return { bg: 'rgba(59, 130, 246, 0.1)', color: '#2563eb' };
  };

  const missingRequiredSecrets = systemInfo.secrets?.filter(s => s.isRequired && !s.isConfigured).length || 0;

  return (
    <Box sx={{ pb: 2 }}>
      {/* Header — icon + heading + one-line description, shared across every admin-options tab */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ComputerIcon color="primary" />
        <Typography variant="h6">{translate('resources.system.title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('resources.system.description')}
      </Typography>

      {/* ── Card 1: System Details ── */}
      <Card sx={cardSx}>
        <CardHeader
          icon={<ComputerIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
          title={translate('resources.system.cards.overview')}
          action={
            <Tooltip title={translate('resources.system.refresh')}>
              <IconButton size="small" onClick={handleRefresh} disabled={refreshing}
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                <RefreshIcon sx={{
                  fontSize: 18,
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                }} />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent sx={{ p: 0 }}>
          <Grid container>
            <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}>
              <Box sx={{ p: 2.5 }}>
                <SectionLabel icon={<ComputerIcon sx={{ fontSize: 16, color: 'text.disabled' }} />} label={translate('resources.system.cards.application')} />
                <InfoRow label={translate('resources.system.fields.version')} value={systemInfo.application.version} />
                <InfoRow label={translate('resources.system.fields.environment')} value={
                  <Chip label={systemInfo.application.environment} size="small" sx={{
                    height: 24, fontSize: '0.75rem', fontWeight: 600, border: 'none',
                    backgroundColor: envChipColor(systemInfo.application.environment).bg,
                    color: envChipColor(systemInfo.application.environment).color,
                  }} />
                } isStatus />
                <InfoRow label={translate('resources.system.fields.uptime')} value={systemInfo.application.uptime} />
                <InfoRow label={translate('resources.system.fields.machineName')} value={describeMachineName(systemInfo.application.machineName)} />
                <InfoRow label={translate('resources.system.fields.processorCount')} value={systemInfo.application.processorCount} />
                <InfoRow label={translate('resources.system.fields.dotnetVersion')} value={formatRuntimeVersion(systemInfo.application.dotnetVersion)} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2.5 }}>
                <SectionLabel icon={<DataObjectIcon sx={{ fontSize: 16, color: 'text.disabled' }} />} label={translate('resources.system.cards.database')} />
                <InfoRow label={translate('resources.system.fields.isConnected')} value={
                  <Chip label={systemInfo.database.isConnected ? translate('resources.system.status.connected') : translate('resources.system.status.disconnected')}
                    size="small" sx={{
                      height: 24, fontSize: '0.75rem', fontWeight: 600, border: 'none',
                      backgroundColor: systemInfo.database.isConnected ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: systemInfo.database.isConnected ? '#16a34a' : '#dc2626',
                    }} />
                } isStatus />
                <InfoRow label={translate('resources.system.fields.databaseSize')} value={systemInfo.database.databaseSize} />
                <InfoRow label={translate('resources.system.fields.provider')} value={describeDatabaseProvider(systemInfo.database.provider)} />
                <InfoRow label={translate('resources.system.fields.appliedMigrations')} value={systemInfo.database.appliedMigrations} />
                <InfoRow label={translate('resources.system.fields.pendingMigrations')} value={
                  <Typography sx={{
                    fontSize: '0.85rem', fontWeight: 500,
                    color: systemInfo.database.pendingMigrations > 0 ? 'error.main' : 'text.primary',
                  }}>
                    {systemInfo.database.pendingMigrations}
                  </Typography>
                } isStatus />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Card 2: Statistics ── */}
      <Card sx={cardSx}>
        <CardHeader icon={<StatsIcon sx={{ fontSize: 20, color: 'primary.main' }} />} title={translate('resources.system.cards.statistics')} />
        <CardContent>
          <Grid container spacing={2}>
            {[
              { label: translate('resources.system.fields.totalDevices'), value: systemInfo.statistics.totalDevices },
              { label: translate('resources.system.fields.onlineDevices'), value: systemInfo.statistics.onlineDevices, color: '#16a34a' },
              { label: translate('resources.system.fields.totalHotels'), value: systemInfo.statistics.totalHotels },
              { label: translate('resources.system.fields.totalGroups'), value: systemInfo.statistics.totalGroups },
              { label: translate('resources.system.fields.totalEventsToday'), value: systemInfo.statistics.totalEventsToday },
              { label: translate('resources.system.fields.totalCheckInsToday'), value: systemInfo.statistics.totalCheckInsToday },
              { label: translate('resources.system.fields.activeAlerts'), value: systemInfo.statistics.activeAlerts, color: systemInfo.statistics.activeAlerts > 0 ? '#dc2626' : undefined },
            ].map((stat, i) => (
              <Grid item xs={6} sm={4} md={3} lg key={i}>
                <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 1, backgroundColor: 'grey.50' }}>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: stat.color || 'text.primary' }}>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* ── Card 3: Secrets & Configuration ── */}
      {systemInfo.secrets && systemInfo.secrets.length > 0 && (
        <Card sx={cardSx}>
          <CardHeader icon={<SecretIcon sx={{ fontSize: 20, color: 'primary.main' }} />} title={translate('resources.system.cards.secrets')} />
          <CardContent>
            {missingRequiredSecrets > 0 && (
              <MuiAlert severity="warning" sx={{ mb: 2, fontSize: '0.85rem' }}>
                {translate('resources.system.secrets.missingRequired', { count: missingRequiredSecrets })}
              </MuiAlert>
            )}
            <Grid container spacing={1}>
              {systemInfo.secrets.map((secret, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1.5,
                    borderRadius: 1, backgroundColor: secret.isConfigured ? 'rgba(34, 197, 94, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                  }}>
                    {secret.isConfigured ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                    ) : (
                      <WarningIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                    )}
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, flex: 1, color: 'text.primary' }}>
                      {describeSecretName(secret.name)}
                    </Typography>
                    <Chip
                      label={secret.isRequired ? translate('resources.system.secrets.required') : translate('resources.system.secrets.optional')}
                      size="small"
                      sx={{
                        height: 20, fontSize: '0.65rem', fontWeight: 600, border: 'none',
                        backgroundColor: secret.isRequired ? 'rgba(59, 130, 246, 0.1)' : 'rgba(156, 163, 175, 0.15)',
                        color: secret.isRequired ? '#2563eb' : '#6b7280',
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* ── Card 4: Logging & Seq ── */}
      {systemInfo.logging && (
        <Card sx={cardSx}>
          <CardHeader icon={<CloudIcon sx={{ fontSize: 20, color: 'primary.main' }} />} title={translate('resources.system.cards.logging')} />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SectionLabel icon={<StorageIcon sx={{ fontSize: 16, color: 'text.disabled' }} />} label={translate('resources.system.logging.sectionLabel')} />
                <InfoRow label={translate('resources.system.logging.seqEnabled')} value={
                  <Chip label={systemInfo.logging.seq.enabled ? 'ON' : 'OFF'} size="small" sx={{
                    height: 22, fontSize: '0.7rem', fontWeight: 700, border: 'none',
                    backgroundColor: systemInfo.logging.seq.enabled ? 'rgba(34, 197, 94, 0.1)' : 'rgba(156, 163, 175, 0.15)',
                    color: systemInfo.logging.seq.enabled ? '#16a34a' : '#6b7280',
                  }} />
                } isStatus />
                {systemInfo.logging.seq.serverUrl && (
                  <InfoRow label={translate('resources.system.logging.seqUrl')} value={
                    <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.primary' }}>
                      {systemInfo.logging.seq.serverUrl}
                    </Typography>
                  } isStatus />
                )}
                <InfoRow label={translate('resources.system.logging.seqApiKey')} value={
                  <Chip label={systemInfo.logging.seq.hasApiKey ? translate('resources.system.secrets.configured') : translate('resources.system.secrets.notConfigured')}
                    size="small" sx={{
                      height: 22, fontSize: '0.7rem', fontWeight: 600, border: 'none',
                      backgroundColor: systemInfo.logging.seq.hasApiKey ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 191, 36, 0.1)',
                      color: systemInfo.logging.seq.hasApiKey ? '#16a34a' : '#ea580c',
                    }} />
                } isStatus />
              </Grid>
            </Grid>

            {/* Seq Filter - copyable */}
            {systemInfo.logging.seqFilter && (
              <Box sx={{
                mt: 2, p: 2, borderRadius: 1, backgroundColor: 'grey.50',
                border: '1px dashed', borderColor: 'divider',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary' }}>
                    {translate('resources.system.logging.seqFilter')}
                  </Typography>
                  <Tooltip title={translate('resources.system.logging.copyFilter')}>
                    <IconButton size="small" onClick={handleCopySeqFilter} sx={{ color: 'primary.main' }}>
                      <CopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography sx={{
                  fontSize: '0.85rem', fontFamily: '"Roboto Mono", monospace', fontWeight: 500,
                  color: 'text.primary', p: 1, borderRadius: 0.5, backgroundColor: 'background.paper',
                  border: '1px solid', borderColor: 'divider',
                }}>
                  {systemInfo.logging.seqFilter}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 1 }}>
                  {translate('resources.system.logging.filterHelp')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Card 5: External Services & Health Checks ── */}
      <Card sx={cardSx}>
        <CardHeader icon={<HealthIcon sx={{ fontSize: 20, color: 'primary.main' }} />} title={translate('resources.system.cards.services')} />
        <CardContent>
          {/* External Services */}
          {systemInfo.externalServices && systemInfo.externalServices.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <SectionLabel icon={<CloudIcon sx={{ fontSize: 16, color: 'text.disabled' }} />} label={translate('resources.system.services.name')} />
              {systemInfo.externalServices.map((svc, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, py: 1,
                  '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
                }}>
                  {svc.isReachable ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                  ) : (
                    <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                  )}
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, flex: 1 }}>{svc.name}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>{svc.url}</Typography>
                  {svc.isReachable && svc.latencyMs != null && (
                    <Chip label={`${svc.latencyMs}ms`} size="small" sx={{
                      height: 20, fontSize: '0.65rem', fontWeight: 600, border: 'none',
                      backgroundColor: svc.latencyMs < 200 ? 'rgba(34, 197, 94, 0.1)' : svc.latencyMs < 1000 ? 'rgba(251, 191, 36, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      color: svc.latencyMs < 200 ? '#16a34a' : svc.latencyMs < 1000 ? '#ea580c' : '#dc2626',
                    }} />
                  )}
                  {!svc.isReachable && svc.error && (
                    <Tooltip title={svc.error}><WarningIcon sx={{ fontSize: 14, color: '#dc2626' }} /></Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Health Checks */}
          {systemInfo.healthChecks && systemInfo.healthChecks.length > 0 && (
            <Box>
              <SectionLabel icon={<HealthIcon sx={{ fontSize: 16, color: 'text.disabled' }} />} label={translate('resources.system.health.name')} />
              {systemInfo.healthChecks.map((check, i) => {
                const statusColor = check.status === 'Healthy' ? '#16a34a' : check.status === 'Degraded' ? '#ea580c' : '#dc2626';
                return (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1,
                    '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: statusColor }} />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, flex: 1 }}>{check.name}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{check.description}</Typography>
                    <Chip label={check.status} size="small" sx={{
                      height: 20, fontSize: '0.65rem', fontWeight: 600, border: 'none',
                      backgroundColor: `${statusColor}15`, color: statusColor,
                    }} />
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Card 6: Background Jobs ── */}
      <Card sx={{ ...cardSx, overflow: 'hidden', mb: 0 }}>
        <CardHeader
          icon={<ScheduleIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
          title={translate('resources.system.cards.jobs')}
          action={
            <Tooltip title={translate('resources.system.refresh')}>
              <IconButton size="small" onClick={handleRefresh} disabled={refreshing}
                sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                <RefreshIcon sx={{
                  fontSize: 18,
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                }} />
              </IconButton>
            </Tooltip>
          }
        />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  translate('resources.system.jobs.name'),
                  translate('resources.system.jobs.queue'),
                  translate('resources.system.jobs.schedule'),
                  translate('resources.system.jobs.lastExecution'),
                  translate('resources.system.jobs.nextExecution'),
                  translate('resources.system.jobs.status'),
                ].map((header) => (
                  <TableCell key={header} sx={tableHeadCellSx}>
                    {header}
                  </TableCell>
                ))}
                <TableCell align="center" sx={tableHeadCellSx}>
                  {translate('resources.system.jobs.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.length > 0 ? jobs.map((job) => {
                const stateColor = job.lastState === 'Succeeded' ? '#16a34a'
                  : job.lastState === 'Failed' ? '#dc2626'
                  : job.lastState === 'Processing' ? '#f59e0b' : '#999';
                const StateIcon = job.lastState === 'Succeeded' ? CheckCircleIcon
                  : job.lastState === 'Failed' ? ErrorIcon
                  : job.lastState === 'Processing' ? ScheduleIcon : WarningIcon;

                return (
                  <TableRow key={job.id} hover sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider' } }}>
                    <TableCell>
                      <Tooltip title={job.description} arrow placement="top-start">
                        <Box>
                          <Typography sx={{ fontWeight: 500, fontSize: '0.85rem', color: 'text.primary' }}>
                            {job.displayName}
                          </Typography>
                          <Typography sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                            {job.id}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={job.queue} size="small" sx={{
                        height: 22, fontSize: '0.7rem', fontWeight: 600, border: 'none',
                        backgroundColor: job.queue === 'critical' ? 'rgba(239, 68, 68, 0.1)' : job.queue === 'low' ? 'rgba(156, 163, 175, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                        color: job.queue === 'critical' ? '#dc2626' : job.queue === 'low' ? '#6b7280' : '#2563eb',
                      }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                        {job.cronDescription}
                      </Typography>
                      <Typography sx={{ color: 'text.disabled', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                        {job.cronExpression}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        {formatDateTime(job.lastExecution)}
                      </Typography>
                      {job.lastDuration && (
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                          {job.lastDuration}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        {formatDateTime(job.nextExecution)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StateIcon sx={{ fontSize: 14, color: stateColor }} />
                        <Typography sx={{ color: stateColor, fontSize: '0.75rem', fontWeight: 500 }}>
                          {job.lastState || translate('resources.system.jobs.neverExecuted')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={translate('resources.system.jobs.trigger')}>
                        <IconButton size="small" onClick={() => handleTriggerJob(job.id, job.displayName)}
                          sx={{ color: 'text.disabled', '&:hover': { backgroundColor: 'action.hover', color: 'primary.main' } }}>
                          <PlayArrowIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {translate('common.messages.no_data')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default SystemInfoView;
